import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import LegacyCharts from '../../../../../ProfitCalculations/Content/SingleProfitCalculation/Result/Charts';
import { ChartType } from '../../../../../ProfitCalculations/types';
import { useResultData } from '../../context';
import { ComponentDimensions } from '../../../../../../types/Layout';

interface Props {
  height: number;
  selectedChartTypes: ChartType[];
  onSelectedChartTypesChange: (next: ChartType[]) => void;
  transactionTypeToggleValues: {
    core: boolean;
    takeProfit: boolean;
    stopLoss: boolean;
  };
  onTransactionTypeToggleValuesChange: (next: {
    core: boolean;
    takeProfit: boolean;
    stopLoss: boolean;
  }) => void;
  onLeftAxisWidthChange?: (width: number) => void;
  onTimeAxisChange?: (info: {
    paddingLeft: number;
    paddingRight: number;
    domainStart: number;
    domainEnd: number;
  }) => void;
}

const noop = () => undefined;

/**
 * The balance/price history maps span the full backtest (often tens of
 * thousands of points). Victory renders every point, so feeding the raw
 * history makes each re-render extremely slow. Evenly sample down to a
 * resolution that is still visually lossless for an on-screen chart.
 */
const downsampleHistory = <T extends Record<string, number> | undefined>(
  record: T,
  max = 800,
): T => {
  if (!record) return record;
  const entries = Object.entries(record);
  if (entries.length <= max) return record;
  const step = Math.ceil(entries.length / max);
  const sampled = entries.filter((_, i) => i % step === 0);
  const last = entries[entries.length - 1];
  if (sampled[sampled.length - 1]?.[0] !== last[0]) {
    sampled.push(last);
  }
  return Object.fromEntries(sampled) as T;
};

const EquityCurve = ({
  height,
  selectedChartTypes,
  onSelectedChartTypesChange,
  transactionTypeToggleValues,
  onTransactionTypeToggleValuesChange,
  onLeftAxisWidthChange,
  onTimeAxisChange,
}: Props) => {
  const { raw } = useResultData();
  const ref = useRef<HTMLDivElement | null>(null);
  const [dimensions, setDimensions] = useState<ComponentDimensions | null>(
    null,
  );

  const chartData = useMemo(
    () => ({
      ...raw,
      endBalanceInUsdtHistory: downsampleHistory(raw.endBalanceInUsdtHistory),
      endBalanceInAssetHistory: downsampleHistory(raw.endBalanceInAssetHistory),
      priceHistory: downsampleHistory(raw.priceHistory),
    }),
    [raw],
  );

  useLayoutEffect(() => {
    if (!ref.current) return;
    const element = ref.current;
    const measure = () =>
      setDimensions({
        width: element.clientWidth,
        height: element.clientHeight,
      });
    measure();
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{ width: '100%', height, display: 'flex', position: 'relative' }}
    >
      {dimensions && (
        <LegacyCharts
          data={chartData}
          allCompactTransactions={raw.compactTransactions ?? raw.transactions}
          rootDimensions={dimensions}
          setIsChartAnimationDone={noop}
          selectedChartTypesOverride={selectedChartTypes}
          onSelectedChartTypesChange={(next) =>
            onSelectedChartTypesChange(
              typeof next === 'function' ? next(selectedChartTypes) : next,
            )
          }
          isTransactionsSelectedOverride={true}
          transactionTypeToggleValuesOverride={transactionTypeToggleValues}
          onTransactionTypeToggleValuesChange={(next) =>
            onTransactionTypeToggleValuesChange(
              typeof next === 'function'
                ? next(transactionTypeToggleValues)
                : next,
            )
          }
          hideControls
          hideXAxis
          hideGrid
          showTimeGridlines
          disableAnimation
          onLeftAxisWidthChange={onLeftAxisWidthChange}
          onTimeAxisChange={onTimeAxisChange}
        />
      )}
    </div>
  );
};

export default EquityCurve;
